import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IServicos } from '@/@types/Protocolo/Servicos';

// components
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AlertDimissible } from '@/components/shared/AlertDimissible';
import { CardHeader } from '@/components/shared/CardHeader';
import { InputWithLabel } from '@/components/shared/InputWithLabel';
import { Button } from '@/components/shared/Button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectGroup, SelectItem, SelectTrigger, SelectValue, SelectContent } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/services/api';

import { ToastAction } from '@radix-ui/react-toast';
import { useToast } from '@/components/ui/use-toast';


// Definindo o schema de validação para o formulário
const formSchema = z.object({
    nome: z.string({ message: 'Campo Obrigatório' }).transform(name => {
        if (typeof name === 'string' && name.length > 0) {
            // Divide a string em palavras
            const words = name.split(' ');
            // Capitaliza a primeira letra de cada palavra
            const capitalizedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
            // Junta as palavras novamente em uma string e retorna
            return capitalizedWords.join(' ');
        }
        // Se não for uma string ou for uma string vazia, retorna sem transformação
        return name;
    }),
    documento: z.string({ message: 'Campo Obrigatório' }),
    telefone: z.string({ message: 'Campo Obrigatório' }),
    email: z.string({ message: 'Campo Obrigatório' }).email({ message: 'Deve ser um email válido' }),
    solicitante: z.string({ message: 'Selecione o tipo de solicitante' }),
    protocoloAnterior: z.string().optional(),
    servicoSelecionado: z.string({ message: 'Selecione o tipo de serviço' }),
    observacao: z.string().optional()
});

type FormSchema = z.infer<typeof formSchema>

interface IFormSolicitacoesProps {
    /** Lista de serviços disponíveis para seleção. */
    servicos: IServicos[];
    /** Tipo de página, 'graduacao' ou 'posgraduacao'. */
    tipoPagina: 'graduacao' | 'posgraduacao';
}

interface INumeroProtocolo {
    _id: string;
    ano: number;
    numero: number;
}


/**
 * Componente para exibição e submissão de formulário de solicitações.
 * @param servicos Lista de serviços disponíveis para seleção.
 * @param tipoPagina Tipo de página, 'graduacao' ou 'posgraduacao'.
 * @returns Componente de formulário de solicitações.
 */

const FormSolicitacoes = ({ servicos, tipoPagina }: IFormSolicitacoesProps) => {
    const { toast } = useToast(); //-> função para toasts

    const [numProtocolo, setNumProtocolo] = useState<INumeroProtocolo>({} as INumeroProtocolo);


    const form = useForm<FormSchema>({
        resolver: zodResolver(formSchema)
    });

    const [date] = useState(
        format(new Date(), 'dd/MM/yyyy')
    );
    const [hora, setHora] = useState('');
    const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);

    useEffect(() => {
        // atualiza o estado time a cada 1 segundo
        const interval = setInterval(() => {
            setHora(format(new Date(), 'HH:mm:ss'));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const getNumService = async () => {
            // busca o número do protocolo
            const url = '/protocolonum/protolocosolicitacoes/all';
            await api.get(url)
                .then(response => {
                    setNumProtocolo(response.data[0]);
                })
                .catch(err => {
                    console.log(err);
                });
        }

        getNumService();
    }, []);

    const updateProtocoloNum = async () => {
        // atualiza o número do protocolo
        const url = `https://proxy-intra.feagri.unicamp.br/protocolonum/protolocosolicitacoes/update/${numProtocolo._id}`;

        await api.put(url, {
            ano: numProtocolo.ano,
            numero: numProtocolo.numero + 1
        })
            .then(response => {
                console.log(response.data);
            })
            .catch(err => {
                console.log(err);
            });
    }

    const onSubmit = async (data: FormSchema) => {
        const formData = new FormData();

        const [{ servico_descricao }] = servicos.filter(servico => servico._id == data.servicoSelecionado);

        formData.set('protocolo_solicitante', data.solicitante);
        formData.set('protocolo_nome', data.nome);
        formData.set('protocolo_documento', data.documento);
        formData.set('protocolo_telefone', data.telefone);
        formData.set('protocolo_email', data.email);
        formData.set('protocolo_servicoid', data.servicoSelecionado);
        formData.set('protocolo_servico', servico_descricao)
        formData.set('protocolo_data', format(new Date(), 'yyyy-MM-dd'));
        formData.set('protocolo_hora', hora);
        formData.set('protocolo_secretaria', tipoPagina);

        arquivoSelecionado && formData.set('fileProtocolo', arquivoSelecionado);
        data.protocoloAnterior && formData.set('protocolo_nanterior', data.protocoloAnterior);
        data.observacao && formData.set('protocolo_complemento', data.observacao);

        formData.set('protocolo_numero', `${numProtocolo.ano}${numProtocolo.numero + 1}`);

        await updateProtocoloNum();

        const url = `/protocolo/solicitacoes/new`;

        await api.post(url, formData)
            .then(response => {
                console.log(response);
                toast({
                    variant: 'default',
                    title: 'Sua solicitação, foi enviada com sucesso!',
                    description: 'Será enviado um e-mail de confirmacão com o número do seu protocolo para consultar o resultado da solicitação.',
                    action: <ToastAction altText="close">  <i className='fa fa-times text-gray-500 hover:text-gray-700 '></i>  </ToastAction>
                });
                resetInputs();
            })
            .catch(err => {
                console.log(err);
                toast({
                    variant: 'destructive',
                    title: 'Erro ao Enviar Solicitação.',
                    description: 'Ocorreu um erro ao enviar sua solicitação. Tente novamente mais tarde!',
                    action: <ToastAction altText="close">  <i className='fa fa-times text-white '></i>  </ToastAction>
                });
            });

    }

    const resetInputs = () => {
        form.reset({
            nome: '',
            documento: '',
            email: '',
            observacao: '',
            telefone: '',
            protocoloAnterior: '',
            solicitante: '',
            servicoSelecionado: ''
        });

    }

    return (
        <>
            <AlertDimissible variant='primary' className='mt-[16px]'>
                <p>
                    Após o preenchimento e envio do Formulário, será enviado um e-mail de confirmacão com o número do seu protocolo para consultar o resultado da solicitação.
                </p>
            </AlertDimissible>


            <div className='md:flex-col md:w-[700px] gap-2'>

                <div className='flex gap-10 mt-10'>
                    <Input
                        readOnly={true}
                        value={date}
                    />
                    <Input
                        readOnly={true}
                        value={hora}
                    />
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>


                        <FormField
                            control={form.control}
                            name='solicitante'
                            render={({ field }) => (
                                
                                    <FormItem className='mt-3'>
                                        <FormLabel>
                                            <p>Solicitante</p>
                                        </FormLabel>
                                        <FormControl>
                                            <Select onValueChange={field.onChange}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder='Selecione' />
                                                </SelectTrigger>

                                                <SelectContent>
                                                    <SelectItem value='Alunos'>Alunos</SelectItem>
                                                    <SelectItem value='Funcionarios'>Funcionários</SelectItem>
                                                    <SelectItem value='Outros'>Outros</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormDescription />
                                        <FormMessage />
                                    </FormItem>
                                
                            )}
                        />



                        <FormField
                            control={form.control}
                            name='nome'
                            render={({ field }) => (
                                
                                    <FormItem className='mt-3'>
                                        <FormLabel>
                                            <p>Nome Completo</p>
                                        </FormLabel>
                                        <FormControl>
                                            <Input placeholder='Digite seu nome completo' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='documento'
                            render={({ field }) => (
                                
                                    <FormItem className='mt-3'>
                                        <FormLabel>
                                            <p>Documento</p>
                                        </FormLabel>
                                        <FormControl>
                                            <Input  {...field} />
                                        </FormControl>
                                        <FormDescription>Preencha somente com números</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='telefone'
                            render={({ field }) => (

                                
                                    <FormItem className='mt-3'>
                                        <FormLabel>
                                            <p>Telefone / Whatsapp</p>
                                        </FormLabel>
                                        <FormControl>
                                            <Input placeholder='(00) 0 0000-0000' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='email'
                            render={({ field }) => (

                                
                                    <FormItem className='mt-3'>
                                        <FormLabel>
                                            <p>Email</p>
                                        </FormLabel>
                                        <FormControl>
                                            <Input placeholder='Digite seu email' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                
                            )}
                        />

                        <Card className='mt-3 mb-3'>
                            <CardHeader>
                                <span className='font-semibold text-lg'>Se necessário, anexar arquivo - <i className='far fa-file-pdf'></i> (.pdf) ou <i className='far fa-file-image'></i> (.jpeg / .png)</span>
                            </CardHeader>
                            <CardContent className='mt-3'>
                                <AlertDimissible variant='warning'>
                                    Recomendação para o arquivo anexo. Exemplo: <span className='font-bold'>documentacao_solicitada.pdf</span> tudo em caixa baixa, sem acentuação e com as palavras unidas por um "_" sublinhado
                                </AlertDimissible>

                                <InputWithLabel
                                    label='Arquivo Anexo'
                                    type='file'
                                    className='text-sm'
                                    onChange={({ target }) => {
                                        target.files && setArquivoSelecionado(target.files[0]);
                                    }}
                                />
                            </CardContent>
                        </Card>

                        <Card className='mt-3 mb-3'>
                            <CardHeader>
                                <span className='text-lg font-semibold'>Se for necessário vincular essa solicitação há um protocolo anterior</span>
                            </CardHeader>
                            <CardContent className='mt-3'>
                                <FormField
                                    control={form.control}
                                    name='protocoloAnterior'
                                    render={({ field }) => (

                                        
                                            <FormItem className='mt-3'>
                                                <FormLabel>
                                                    <p>Protocolo Anterior</p>
                                                </FormLabel>
                                                <FormControl>
                                                    <Input {...field} />

                                                </FormControl>
                                                <FormDescription>Somente o número do protocolo</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        
                                    )}
                                />

                            </CardContent>
                        </Card>

                        <Card className='mt-3 mb-3'>
                            <CardHeader>
                                <span className='text-lg font-semibold'>Escolha o Serviço</span>
                            </CardHeader>
                            <CardContent className='mt-3'>

                                <FormField
                                    control={form.control}
                                    name='servicoSelecionado'
                                    render={({ field }) => (

                                        <FormItem className='mt-3'>
                                            <FormLabel>
                                                <p>Serviço</p>
                                            </FormLabel>
                                            <FormControl>
                                                <Select onValueChange={field.onChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder='Selecione o serviço' />
                                                    </SelectTrigger>

                                                    <SelectContent>
                                                        <SelectGroup>
                                                            {servicos.map((servico) => {
                                                                return <SelectItem value={servico._id} key={servico._id}>{servico.servico_descricao}</SelectItem>
                                                            })}
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name='observacao'
                                    render={({ field }) => (
                                        
                                            <FormItem className='mt-3'>
                                                <FormLabel>
                                                    <p>Observação / Complemento</p>
                                                </FormLabel>
                                                <FormControl>
                                                    <Textarea placeholder='Descrição / complemento do serviço solicitado acima, se necessário.' {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        

                                    )}
                                />
                            </CardContent>
                        </Card>

                        <Button variant='primary' className='flex gap-2 justify-between'>
                            <i className='fas fa-share-square'></i>
                            Enviar
                        </Button>

                    </form>
                </Form>


            </div >
        </>
    )
}

export { FormSolicitacoes };